import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { technicalAPI } from '../../services/api';
import { SeverityBadge } from './TechnicalUI';

// Bell dropdown of active alarms — the technical role's equivalent of the admin
// NotificationBell (which is payment-sourced and not relevant here). Badge count is
// simply the number of currently-active alarms, not a "newer than last seen" diff like
// the payment bell — an alarm staying active because nobody's fixed it yet should keep
// showing, not disappear the moment someone opens the dropdown.
const POLL_MS = 30000;

export default function TechnicalNotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [alarms, setAlarms] = useState([]);
  const ref = useRef(null);

  // Fetch every active, unacknowledged alarm (not just the ones shown) so the badge count
  // is the real total, not just however many happened to be in the dropdown's display slice
  // — an earlier version capped the request at limit:8 and showed that as the count, which
  // silently underreported once active alarms passed 8. `unacknowledged` only affects this
  // bell — the full Alarms page keeps showing everything regardless of ack state.
  //
  // Critical + major only. A notification badge is a claim on someone's attention, and most
  // active alarms are customers who switched their own power off — a permanently-lit bell
  // showing 32 routine power cuts is one nobody looks at. Those are still on the Alarms page.
  const load = useCallback(() => {
    technicalAPI.getAlarms({ status: 'active', unacknowledged: 'true', severity: 'critical,major' })
      .then(({ data }) => setAlarms(data.data || [])).catch(() => {});
  }, []);

  // Acknowledging only dismisses from this bell — the alarm stays active and still shows
  // on the full Alarms page (see Alarm model comment); it can still resolve for real later.
  const acknowledge = (id, e) => {
    e.stopPropagation();
    setAlarms(a => a.filter(x => x._id !== id));
    technicalAPI.acknowledgeAlarm(id).catch(() => { toast.error('Failed to clear alarm'); load(); });
  };

  const clearAll = () => {
    const ids = alarms.map(a => a._id);
    setAlarms([]);
    technicalAPI.acknowledgeAllAlarms().catch(() => { toast.error('Failed to clear alarms'); load(); });
    if (ids.length) toast.success(`Cleared ${ids.length} alarm${ids.length === 1 ? '' : 's'} from notifications`);
  };

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    const onClickOutside = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ position: 'relative', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 6, display: 'flex' }}
        title="Alarms"
      >
        <Bell size={18} />
        {alarms.length > 0 && (
          <span style={{
            position: 'absolute', top: 2, right: 2, minWidth: 15, height: 15, borderRadius: 8,
            background: 'var(--cb-d03b3b)', color: 'var(--c-ffffff)', fontSize: '.6rem', fontWeight: 700,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 3px',
          }}>
            {alarms.length > 9 ? '9+' : alarms.length}
          </span>
        )}
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: '100%', right: 0, marginTop: 8, width: 300, maxHeight: 360, overflowY: 'auto',
          background: 'var(--bg-card, #fff)', border: '1px solid var(--border)', borderRadius: 10,
          boxShadow: '0 10px 30px rgba(0,0,0,.15)', zIndex: 200,
        }}>
          <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontWeight: 700, fontSize: '.85rem' }}>Active Alarms</span>
            {alarms.length > 0 && (
              <button onClick={clearAll} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--primary)', fontSize: '.76rem', fontWeight: 600 }}>
                Clear All
              </button>
            )}
          </div>
          {alarms.length === 0 ? (
            <div style={{ padding: '20px 14px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '.82rem' }}>
              No active alarms — all clear.
            </div>
          ) : (
            alarms.slice(0, 8).map(a => (
              <div
                key={a._id}
                role="button" tabIndex={0}
                onClick={() => { setOpen(false); navigate('/technical/alarms'); }}
                style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', cursor: 'pointer', display: 'flex', gap: 8 }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 3 }}>
                    <SeverityBadge severity={a.severity} />
                  </div>
                  <div style={{ fontSize: '.8rem' }}>{a.message}</div>
                </div>
                <button
                  onClick={(e) => acknowledge(a._id, e)}
                  title="Clear this notification"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 2, flexShrink: 0, height: 'fit-content' }}
                >
                  <X size={14} />
                </button>
              </div>
            ))
          )}
          <button
            onClick={() => { setOpen(false); navigate('/technical/alarms'); }}
            style={{ width: '100%', padding: '10px 14px', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--primary)', fontSize: '.82rem', fontWeight: 600, textAlign: 'center' }}
          >
            View All Alarms
          </button>
        </div>
      )}
    </div>
  );
}
